import { useState } from "react";
import { PhoneCall } from "lucide-react";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { MarkdownView } from "../MarkdownEditor";
import { PHONE_DISPLAY, PHONE_HREF } from "./HomeTopBar";

// Keys match the fields of GET /public/home-explainers, which the superadmin
// fills in from Ajustes → "Ventanas del menú de la home".
const MENU = [
  { key: "cursos", label: "Cursos" },
  { key: "talleres", label: "Talleres" },
  { key: "metodologia", label: "Metodología" },
];

/** Centred logo plus the menu: three explainer popups and «Te llamamos». */
export default function HomeNav({ explainers, onContact }) {
  const [openKey, setOpenKey] = useState(null);
  const current = MENU.find((m) => m.key === openKey);
  const text = (openKey && explainers?.[openKey]) || "";

  return (
    <header className="bg-white border-b border-gray-200">
      <div className="flex flex-col items-center gap-1.5 sm:gap-2 pt-6 sm:pt-8 px-4">
        {/* multiply: the JPEG's off-white background melts into the header. */}
        <img src="/logo.jpg" alt="laclaseencasa" className="h-[76px] sm:h-[104px] w-auto mix-blend-multiply" />
        <p className="text-[10px] sm:text-[11px] tracking-[0.28em] uppercase text-gray-500 font-mono-data">
          de SIMA Asistencial
        </p>
      </div>

      <nav
        className="max-w-3xl mx-auto grid grid-cols-2 sm:flex sm:justify-center sm:items-center gap-2 sm:gap-1.5 px-4 py-5"
        data-testid="home-nav"
      >
        {MENU.map((m) => (
          <button
            key={m.key}
            type="button"
            onClick={() => setOpenKey(m.key)}
            className="h-11 sm:px-5 flex items-center justify-center text-xs sm:text-[13px] font-semibold uppercase tracking-[0.16em] sm:tracking-[0.18em] text-brand-ink border border-gray-200 rounded-lg sm:border-0 sm:border-b-2 sm:border-transparent sm:rounded-none hover:text-brand sm:hover:border-brand transition-colors"
            data-testid={`nav-${m.key}`}
          >
            {m.label}
          </button>
        ))}
        <button
          type="button"
          onClick={onContact}
          className="h-11 sm:ml-2.5 sm:px-5 flex items-center justify-center gap-2 text-xs sm:text-[13px] font-semibold uppercase tracking-[0.16em] sm:tracking-[0.18em] text-brand-ink bg-brand-soft border-[1.5px] border-brand rounded-lg sm:rounded-full hover:bg-brand hover:text-white transition-colors"
          data-testid="nav-te-llamamos"
        >
          <PhoneCall className="w-[15px] h-[15px]" />
          Te llamamos
        </button>
      </nav>

      <Dialog open={!!current} onOpenChange={(o) => !o && setOpenKey(null)}>
        <DialogContent className="max-w-lg p-0 gap-0 overflow-hidden" data-testid="explainer-modal">
          <div className="bg-brand-soft px-7 pt-6 pb-5">
            <p className="text-[11px] tracking-[0.25em] uppercase text-brand font-mono-data mb-1.5">
              laclaseencasa
            </p>
            <DialogHeader>
              <DialogTitle className="font-display text-3xl text-brand-ink">{current?.label}</DialogTitle>
            </DialogHeader>
          </div>
          <div className="px-7 py-6 max-h-[60vh] overflow-y-auto" data-testid="explainer-content">
            {text.trim() ? (
              <MarkdownView text={text} />
            ) : (
              <p className="text-sm text-gray-600">
                Llámanos al{" "}
                <a href={PHONE_HREF} className="font-semibold text-brand-ink hover:text-brand">
                  {PHONE_DISPLAY}
                </a>{" "}
                y te lo contamos.
              </p>
            )}
          </div>
          <div className="px-7 py-4 border-t border-gray-100 flex items-center justify-between gap-3 flex-wrap">
            <span className="text-sm text-gray-500">¿Tienes dudas?</span>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setOpenKey(null)}>
                Cerrar
              </Button>
              <Button
                className="bg-brand hover:bg-brand-hover text-white"
                onClick={() => {
                  setOpenKey(null);
                  onContact();
                }}
                data-testid="explainer-contact"
              >
                <PhoneCall className="w-4 h-4 mr-2" />
                Te llamamos
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </header>
  );
}
